import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useLocation, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { ArrowRight, BookOpen } from 'lucide-react';
import { Course } from '../hooks/useCourseApi';
import CourseCard from '../components/course/CourseCard';
import CourseCardSkeleton from '../components/common/CourseCardSkeleton';
import StandaloneUnitsSection from '../components/unit/StandaloneUnitsSection';
import SEOHead from '../components/common/SEOHead';
import axios from 'axios';
import { BASE_URL } from '../apiConfig';
import { getTenantHeaders } from '../config/tenant';
import {
  BRAND_FULL_NAME,
  BRAND_KEYWORDS,
  BRAND_NAME,
  BRAND_TEACHER_NAME,
  GRADE_SEO,
  SITE_URL,
} from '../config/brand';
import { getCoursePath } from '../utils/courseSlug';
import { teacherGradePath } from '../utils/teacherRoutes';

interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data?: T;
}

function mapApiCourseToCourse(apiCourse: Record<string, unknown>): Course {
  return {
    id: String(apiCourse.id ?? apiCourse.Id ?? ''),
    courseName: String(apiCourse.courseName ?? apiCourse.CourseName ?? ''),
    term: String(apiCourse.term ?? apiCourse.Term ?? ''),
    active: Boolean(apiCourse.active ?? apiCourse.Active ?? true),
    price: Number(apiCourse.price ?? apiCourse.Price ?? 0),
    grade: String(apiCourse.grade ?? apiCourse.Grade ?? ''),
    description: String(apiCourse.description ?? apiCourse.Description ?? ''),
    imagePath: String(apiCourse.imagePath ?? apiCourse.ImagePath ?? ''),
    modificationDate: String(apiCourse.modificationDate ?? apiCourse.ModificationDate ?? ''),
  };
}

const COURSE_PAGE_SIZE = 10;
const SUBJECT_FETCH_PAGE_SIZE = 100;

function extractCoursesList(data: unknown): Record<string, unknown>[] {
  if (Array.isArray(data)) return data as Record<string, unknown>[];
  if (!data || typeof data !== 'object') return [];
  const raw = data as Record<string, unknown>;
  if (Array.isArray(raw.courses)) return raw.courses as Record<string, unknown>[];
  if (Array.isArray(raw.Courses)) return raw.Courses as Record<string, unknown>[];
  if (Array.isArray(raw.data)) return raw.data as Record<string, unknown>[];
  if (Array.isArray(raw.Data)) return raw.Data as Record<string, unknown>[];
  return [];
}

function extractPagination(data: unknown, fallbackCount: number, pageSize: number) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    return {
      totalPages: Math.max(1, Math.ceil(fallbackCount / pageSize) || 1),
      totalCount: fallbackCount,
    };
  }
  const raw = data as Record<string, unknown>;
  const totalCount = Number(raw.totalCount ?? raw.TotalCount ?? fallbackCount);
  const totalPages = Number(
    raw.totalPages ?? raw.TotalPages ?? Math.max(1, Math.ceil(totalCount / pageSize) || 1)
  );
  return {
    totalPages: Number.isFinite(totalPages) && totalPages > 0 ? totalPages : 1,
    totalCount: Number.isFinite(totalCount) ? totalCount : fallbackCount,
  };
}

function extractSubjectIds(state: Record<string, unknown> | null): string[] {
  if (!state) return [];
  const ids: string[] = [];
  const add = (value: unknown) => {
    const id = String(value ?? '').trim();
    if (id && !ids.includes(id)) ids.push(id);
  };
  add(state.subjectId);
  if (Array.isArray(state.subjectIds)) {
    for (const id of state.subjectIds) add(id);
  }
  if (Array.isArray(state.subjects)) {
    for (const subject of state.subjects) {
      if (subject && typeof subject === 'object') {
        const item = subject as { id?: string; Id?: string };
        add(item.id ?? item.Id);
      }
    }
  }
  return ids;
}

function resolveGradeValue(gradeParam: string | undefined, stateGrade: unknown): string {
  if (typeof stateGrade === 'string' && stateGrade.trim()) return stateGrade;
  const key = (gradeParam || '').toLowerCase();
  const map: Record<string, string> = {
    secondary1: 'Secondary1',
    secondary2: 'Secondary2',
    secondary3: 'Secondary3',
    prep1: 'Prep1',
    prep2: 'Prep2',
    prep3: 'Prep3',
  };
  return map[key] ?? 'Secondary1';
}

function getGradeTitle(gradeValue: string): string {
  const titles: Record<string, string> = {
    Secondary1: 'الصف الأول الثانوي',
    Secondary2: 'الصف الثاني الثانوي',
    Secondary3: 'الصف الثالث الثانوي',
    Prep1: 'الصف الأول الإعدادي',
    Prep2: 'الصف الثاني الإعدادي',
    Prep3: 'الصف الثالث الإعدادي',
  };
  return titles[gradeValue] ?? 'الصف الأول الثانوي';
}

function courseBelongsToGrade(courseGrade: string | undefined, gradeValue: string): boolean {
  if (!courseGrade) return true;
  if (courseGrade.toLowerCase() === gradeValue.toLowerCase()) return true;
  const title = getGradeTitle(gradeValue);
  if (courseGrade.includes(title) || title.includes(courseGrade)) return true;
  const fragments: Record<string, string[]> = {
    Secondary1: ['الأول الثانوي', 'أولى ثانوي'],
    Secondary2: ['الثاني الثانوي', 'ثانية ثانوي'],
    Secondary3: ['الثالث الثانوي', 'ثالثة ثانوي', 'تالتة ثانوي'],
    Prep1: ['الأول الإعدادي', 'أولى إعدادي'],
    Prep2: ['الثاني الإعدادي', 'ثانية إعدادي'],
    Prep3: ['الثالث الإعدادي', 'ثالثة إعدادي'],
  };
  return (fragments[gradeValue] || []).some((fragment) => courseGrade.includes(fragment));
}

function mergeUniqueCourses(groups: Course[][]): Course[] {
  const byId = new Map<string, Course>();
  for (const group of groups) {
    for (const course of group) {
      if (course.id && !byId.has(course.id)) byId.set(course.id, course);
    }
  }
  return Array.from(byId.values());
}

async function requestTeacherCourses(
  teacherId: string,
  options: { subjectId?: string; pageNumber: number; pageSize: number }
): Promise<{ success: boolean; courses: Course[]; totalPages: number; totalCount: number }> {
  const params: Record<string, string | number | boolean> = {
    pageNumber: options.pageNumber,
    pageSize: options.pageSize,
    teacherid: teacherId,
    active: true,
  };
  if (options.subjectId) {
    params.subjectid = options.subjectId;
  }

  const response = await axios.get<ApiResponse<unknown>>(`${BASE_URL}api/Course/all`, {
    params,
    headers: { ...getTenantHeaders() },
  });

  if (!response.data?.success || response.data.data == null) {
    return { success: false, courses: [], totalPages: 1, totalCount: 0 };
  }

  const courses = extractCoursesList(response.data.data).map(mapApiCourseToCourse);
  const pagination = extractPagination(response.data.data, courses.length, options.pageSize);
  return { success: true, courses, ...pagination };
}

async function requestAllCoursesForSubject(teacherId: string, subjectId: string): Promise<Course[]> {
  const first = await requestTeacherCourses(teacherId, {
    subjectId,
    pageNumber: 1,
    pageSize: SUBJECT_FETCH_PAGE_SIZE,
  });
  const courses = [...first.courses];
  for (let page = 2; page <= first.totalPages; page += 1) {
    const next = await requestTeacherCourses(teacherId, {
      subjectId,
      pageNumber: page,
      pageSize: SUBJECT_FETCH_PAGE_SIZE,
    });
    courses.push(...next.courses);
  }
  return mergeUniqueCourses([courses]);
}

const GradeCoursesPage: React.FC = () => {
  const { grade, teacherId: teacherIdParam } = useParams<{ grade: string; teacherId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [courses, setCourses] = useState<Course[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reloadRef = useRef<() => void>(() => undefined);
  const navigationKeyRef = useRef(location.key);

  // Get grade from params or location state
  const gradeValue = resolveGradeValue(grade, location.state?.grade);
  const gradeTitle = getGradeTitle(gradeValue);

  // Pinned course ID for Secondary3 grade page
  const PINNED_COURSE_ID = '1';

  const subjectName = location.state?.subjectName;
  const teacherId = (
    teacherIdParam ||
    searchParams.get('teacherId') ||
    (location.state?.teacherId as string | undefined) ||
    ''
  ).trim() || undefined;
  const fromSubject = location.state?.fromSubject;
  const fromStudentPortal = location.state?.fromStudentPortal;

  // Public Course/tree — works for guests (no auth axios / no login redirect)
  const ensurePinnedSecondary3Course = useCallback(
    async (currentCourses: Course[]): Promise<Course[]> => {
      if (gradeValue !== 'Secondary3') {
        return currentCourses;
      }

      const existingPinned = currentCourses.find((c) => c.id === PINNED_COURSE_ID);
      if (existingPinned) {
        const others = currentCourses.filter((c) => c.id !== PINNED_COURSE_ID);
        return [existingPinned, ...others];
      }

      try {
        const response = await axios.get<ApiResponse<Course>>(`${BASE_URL}api/Course/tree`, {
          params: { courseid: PINNED_COURSE_ID },
          headers: { ...getTenantHeaders() },
        });
        if (response.data?.success && response.data.data) {
          return [mapApiCourseToCourse(response.data.data as unknown as Record<string, unknown>), ...currentCourses];
        }
      } catch (e) {
        console.error('Error fetching pinned Secondary3 course:', e);
      }

      return currentCourses;
    },
    [gradeValue]
  );

  /** Public teacher/subject courses — used after grades page for guests + auth */
  const fetchTeacherCourses = useCallback(
    async (finalTeacherId: string, subjectIds: string[]) => {
      try {
        setIsLoading(true);
        setError(null);

        const uniqueSubjectIds = Array.from(new Set(subjectIds.filter(Boolean)));

        if (uniqueSubjectIds.length === 0) {
          const result = await requestTeacherCourses(finalTeacherId, {
            pageNumber: 1,
            pageSize: SUBJECT_FETCH_PAGE_SIZE,
          });
          const mappedCourses = result.courses.filter((c) => courseBelongsToGrade(c.grade, gradeValue));
          const coursesWithPinned = await ensurePinnedSecondary3Course(mappedCourses);
          const start = (pageNumber - 1) * COURSE_PAGE_SIZE;
          setCourses(coursesWithPinned.slice(start, start + COURSE_PAGE_SIZE));
          setTotalPages(Math.max(1, Math.ceil(coursesWithPinned.length / COURSE_PAGE_SIZE) || 1));
          return;
        }

        if (uniqueSubjectIds.length > 1) {
          const groups = await Promise.all(
            uniqueSubjectIds.map((subjectId) => requestAllCoursesForSubject(finalTeacherId, subjectId))
          );
          const merged = mergeUniqueCourses(groups);
          const coursesWithPinned = await ensurePinnedSecondary3Course(merged);
          const start = (pageNumber - 1) * COURSE_PAGE_SIZE;
          setCourses(coursesWithPinned.slice(start, start + COURSE_PAGE_SIZE));
          setTotalPages(Math.max(1, Math.ceil(coursesWithPinned.length / COURSE_PAGE_SIZE) || 1));
          return;
        }

        const subjectId = uniqueSubjectIds[0];
        const result = await requestTeacherCourses(finalTeacherId, {
          subjectId,
          pageNumber,
          pageSize: COURSE_PAGE_SIZE,
        });

        if (!result.success) {
          setCourses([]);
          setTotalPages(1);
          setError('لا توجد كورسات متاحة');
          return;
        }

        const coursesWithPinned = await ensurePinnedSecondary3Course(result.courses);
        setCourses(coursesWithPinned);
        setTotalPages(result.totalPages);
      } catch (err) {
        console.error('Error fetching teacher courses:', err);
        setError('حدث خطأ أثناء جلب الكورسات');
        setCourses([]);
        setTotalPages(1);
      } finally {
        setIsLoading(false);
      }
    },
    [pageNumber, gradeValue, ensurePinnedSecondary3Course]
  );

  /** Public grade listing — no auth required so guests can browse after grades */
  const fetchGradeCourses = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await axios.get<ApiResponse<{
        totalPages?: number;
        courses?: Course[];
      }>>(`${BASE_URL}api/AdminStudent/CourseActive`, {
        params: {
          grade: gradeValue,
          pagenumber: pageNumber,
          pagesize: 10,
        },
        headers: { ...getTenantHeaders() },
      });

      if (response.data?.success && response.data?.data) {
        const fetchedCourses = (response.data.data.courses || []).map((c) =>
          mapApiCourseToCourse(c as unknown as Record<string, unknown>)
        );
        const coursesWithPinned = await ensurePinnedSecondary3Course(fetchedCourses);
        setCourses(coursesWithPinned);
        setTotalPages(response.data.data.totalPages || 1);
      } else {
        setCourses([]);
        setTotalPages(1);
      }
    } catch (err) {
      console.error('Error fetching grade courses:', err);
      setError('حدث خطأ أثناء جلب الكورسات');
      setCourses([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  }, [gradeValue, pageNumber, ensurePinnedSecondary3Course]);

  // Put teacher id in the URL so the grade page can be shared and reopened.
  useEffect(() => {
    if (!teacherId || teacherIdParam) return;
    navigate(teacherGradePath(teacherId, grade || gradeValue), {
      replace: true,
      state: location.state,
    });
  }, [teacherId, teacherIdParam, grade, gradeValue, navigate, location.state]);

  // Main effect — public fetch so unauthorized users can open courses after grades
  useEffect(() => {
    const isNewNavigation = navigationKeyRef.current !== location.key;
    navigationKeyRef.current = location.key;
    if (isNewNavigation && pageNumber !== 1) {
      setPageNumber(1);
      return;
    }

    const subjectIds = extractSubjectIds(location.state as Record<string, unknown> | null);

    const load = () => {
      if (teacherId) {
        fetchTeacherCourses(teacherId, subjectIds);
      } else {
        fetchGradeCourses();
      }
    };

    reloadRef.current = load;
    load();
  }, [location.pathname, location.key, location.state, teacherId, gradeValue, pageNumber, fetchTeacherCourses, fetchGradeCourses]);

  const handlePageChange = (newPage: number) => {
    setPageNumber(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const gradeSlug = (grade || gradeValue || 'secondary1').toLowerCase();
  const gradeSeo = GRADE_SEO[gradeSlug];
  const seoPath = teacherId ? teacherGradePath(teacherId, gradeSlug) : `/courses/grade/${gradeSlug}`;
  const teacherBackPath = teacherId
    ? fromStudentPortal
      ? `/student-portal/teacher/${teacherId}`
      : `/teacher/${teacherId}`
    : fromStudentPortal
      ? '/student-portal'
      : '/';
  const seoTitle = `كورسات ${gradeSeo?.title || gradeTitle} أونلاين | ${BRAND_NAME} | ${BRAND_TEACHER_NAME}`;
  const seoDescription =
    `تصفّح واشترك في كورسات ${gradeSeo?.title || gradeTitle} على منصة نهج الأزهر مع الأستاذ سعيد الفقي — لغة عربية وعلوم شرعية أونلاين لطلاب الثانوية الأزهرية.` +
    (courses.length > 0 ? ` متاح حالياً ${courses.length} كورس.` : '');
  const seoKeywords = [
    BRAND_KEYWORDS,
    gradeSeo?.keywords,
    `كورسات ${gradeTitle}`,
    'كورسات أزهرية أونلاين',
    BRAND_TEACHER_NAME,
  ]
    .filter(Boolean)
    .join(', ');

  const gradeStructuredData = useMemo(() => {
    const collectionPage = {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}${seoPath}#webpage`,
      name: seoTitle,
      description: seoDescription,
      url: `${SITE_URL}${seoPath}`,
      inLanguage: 'ar',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: [
        { '@type': 'Thing', name: gradeSeo?.title || gradeTitle },
        { '@type': 'Thing', name: 'نهج الأزهر' },
        { '@type': 'Thing', name: BRAND_TEACHER_NAME },
      ],
      provider: {
        '@type': 'EducationalOrganization',
        name: BRAND_FULL_NAME,
        url: SITE_URL,
      },
    };

    if (courses.length === 0) return collectionPage;

    const itemList = {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: `كورسات ${gradeSeo?.title || gradeTitle}`,
      numberOfItems: courses.length,
      itemListElement: courses.slice(0, 20).map((course, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        url: `${SITE_URL}${getCoursePath(course)}`,
        name: course.courseName,
        item: {
          '@type': 'Course',
          name: course.courseName,
          description: course.description || `كورس ${course.courseName} على ${BRAND_NAME}`,
          url: `${SITE_URL}${getCoursePath(course)}`,
          provider: {
            '@type': 'EducationalOrganization',
            name: BRAND_FULL_NAME,
            url: SITE_URL,
          },
          offers: {
            '@type': 'Offer',
            price: course.price ?? 0,
            priceCurrency: 'EGP',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}${getCoursePath(course)}`,
          },
        },
      })),
    };

    return [collectionPage, itemList];
  }, [courses, gradeSeo?.title, gradeTitle, seoDescription, seoPath, seoTitle]);

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        url={seoPath}
        breadcrumbs={[
          { name: teacherId ? 'المدرس' : 'الكورسات', path: teacherId ? `/teacher/${teacherId}` : seoPath },
          { name: gradeSeo?.title || gradeTitle, path: seoPath },
        ]}
        structuredData={gradeStructuredData}
      />
      {/* Header Section */}
      <div className="py-8 border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center space-x-2 text-gray-500 mb-8" dir="ltr">
            {isLoading ? (
              <div className="flex items-center space-x-2 w-full">
                <div className="h-4 w-20 bg-gray-200 rounded-md relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
                <div className="w-4 h-4 mx-2 bg-gray-200 rounded-full relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
                <div className="h-4 w-32 bg-gray-200 rounded-md relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
              </div>
            ) : (
              <>
                <Link 
                  to={teacherBackPath} 
                  className="hover:text-orange-500 transition-colors duration-200 flex items-center"
                >
                  {teacherId
                    ? 'صفحة المدرس'
                    : fromStudentPortal
                      ? 'الحصص والكورسات'
                      : 'الرئيسية'}
                  <ArrowRight className="w-4 h-4 mx-2" />
                </Link>
                <span className="text-gray-900">
                  {teacherId
                    ? fromSubject && subjectName
                      ? `كورسات وحصص ${subjectName}`
                      : `كورسات وحصص ${gradeTitle}`
                    : fromSubject && subjectName
                    ? `كورسات ${subjectName}`
                    : `كورسات ${gradeTitle}`}
                </span>
              </>
            )}
          </nav>

          {/* Page Title */}
          <div className="text-center">
            {isLoading ? (
              <div className="flex flex-col items-center">
                <div className="h-12 w-64 bg-gray-200 rounded-lg mb-4 mx-auto relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
                <div className="h-4 w-48 bg-gray-200 rounded-md mx-auto relative overflow-hidden">
                  <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200"></div>
                </div>
              </div>
            ) : (
              <h1 className="text-4xl md:text-5xl font-cairo-bold text-gray-900 mb-4">
                {teacherId
                  ? fromSubject && subjectName
                    ? `كورسات وحصص ${subjectName}`
                    : `كورسات وحصص ${gradeTitle}`
                  : fromSubject && subjectName
                  ? `كورسات ${subjectName}`
                  : `كورسات ${gradeTitle}`}
              </h1>
            )}
          </div>
        </div>
      </div>

      {/* Courses Section */}
      <div className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {isLoading && (
            <div className="py-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {Array.from({ length: 6 }).map((_, index) => (
                  <CourseCardSkeleton key={index} />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="text-center py-12">
              <div className="bg-white border border-gray-200 rounded-lg p-6 max-w-md mx-auto shadow-sm">
                <p className="text-gray-800 font-cairo-medium mb-4">{error}</p>
                <button
                  type="button"
                  onClick={() => reloadRef.current()}
                  className="px-6 py-2 bg-white border border-orange-500 text-orange-500 rounded-lg hover:bg-orange-50 transition-colors duration-200 font-cairo-medium"
                >
                  إعادة المحاولة
                </button>
              </div>
            </div>
          )}

          {!isLoading && !error && courses.length === 0 && (
            <div className="text-center py-12">
              <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500 font-cairo-medium">
                لا توجد كورسات متاحة لـ{gradeTitle} حالياً
              </p>
            </div>
          )}

          {!isLoading && !error && courses.length > 0 && (
            <>
              {/* Courses Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                {courses.map((course) => (
                  <CourseCard key={course.id} course={course} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-10">
                  <div className="inline-flex rounded-lg border border-gray-200 divide-x rtl:divide-x-reverse">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`px-4 py-2 transition-colors duration-200 font-cairo-medium ${
                          pageNumber === page
                            ? 'bg-orange-50 text-orange-600'
                            : 'bg-white text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {teacherId && (
        <StandaloneUnitsSection
          teacherId={teacherId}
          title="حصص المدرس"
          subtitle="حصص مستقلة يمكن شراؤها مباشرة بدون اشتراك في كورس كامل"
          className="py-8 sm:py-12 bg-gradient-to-b from-white to-gold-50/20 border-t border-gray-100"
        />
      )}
    </div>
  );
};

export default GradeCoursesPage;
